<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  createCustomer,
  deleteCustomer,
  listCustomers,
  updateCustomer,
  type Customer,
  type CustomerInput,
} from '../api/customers'

const rows = ref<Customer[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Customer | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<CustomerInput>({
  code: '',
  name: '',
  contact: '',
  phone: '',
  address: '',
  status: 'active',
})

const codeError = computed(() => (form.code.trim() ? '' : '请输入客户编码'))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listCustomers({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  editing.value = null
  Object.assign(form, { code: '', name: '', contact: '', phone: '', address: '', status: 'active' })
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Customer): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    contact: row.contact,
    phone: row.phone,
    address: row.address,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (codeError.value) return
  saving.value = true
  formError.value = null
  const input: CustomerInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    contact: form.contact.trim(),
    phone: form.phone.trim(),
    address: form.address.trim(),
    status: form.status,
  }
  try {
    if (editing.value) await updateCustomer(editing.value.id, input)
    else await createCustomer(input)
    formOpen.value = false
    await load(editing.value ? '客户已更新' : '客户已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Customer): Promise<void> {
  notice.value = ''
  try {
    await deleteCustomer(row.id)
    await load(`已删除客户「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-cus">
    <header class="yc-cus__bar">
      <el-input
        v-model="query.keyword"
        class="yc-cus__keyword"
        placeholder="编码 / 名称"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-cus__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:customer:create'" type="primary" @click="openCreate">新建客户</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-cus__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-cus__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-cus__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="客户编码" min-width="140" />
        <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="contact" label="联系人" width="120" />
        <el-table-column prop="phone" label="电话" min-width="150" show-overflow-tooltip />
        <el-table-column prop="address" label="地址" min-width="200" show-overflow-tooltip />
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ STATUS_LABELS[row.status as Customer['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="160">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'wms:customer:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该客户？" width="200" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'wms:customer:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有客户"
        description="出库单要选客户（收货方），先建一个。"
      >
        <el-button v-perm="'wms:customer:create'" type="primary" @click="openCreate">新建客户</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-cus__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑客户 · ${editing.code}` : '新建客户'"
      width="520px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-cus__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="客户编码" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 CUS001" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 青浦门店" />
        </el-form-item>

        <el-form-item label="联系人">
          <el-input v-model="form.contact" maxlength="32" placeholder="如 李四" />
        </el-form-item>

        <el-form-item label="电话">
          <el-input v-model="form.phone" maxlength="32" placeholder="如 13900139000" />
        </el-form-item>

        <el-form-item label="地址">
          <el-input v-model="form.address" maxlength="255" placeholder="如 上海市青浦区 xx 路 1 号" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!codeError">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-cus {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
